import {
    collectReferenceMentionOccurrences,
    getBrokenReferenceTargets,
} from "@/utils/reference-mentions";

import {
    normalizeSkillEditorText,
    SKILL_INDEX_CLIENT_KEY,
    type SkillEditorState,
} from "./skill-editor-state";

export { SKILL_INDEX_CLIENT_KEY };

export interface SkillEditorReferenceOccurrence {
    readonly sourceClientKey: string;
    readonly typedTarget: string;
    readonly start: number;
    readonly end: number;
    readonly resolvedTargetClientKey: string | null;
}

export interface SkillEditorBrokenReferenceTarget {
    readonly sourceClientKey: string;
    readonly typedTarget: string;
}

export interface SkillEditorInboundReferenceSource {
    readonly targetClientKey: string;
    readonly sourceClientKey: string;
    readonly occurrenceCount: number;
}

export interface SkillEditorReferences {
    readonly vocabulary: readonly string[];
    readonly occurrences: readonly SkillEditorReferenceOccurrence[];
    readonly brokenTargets: readonly SkillEditorBrokenReferenceTarget[];
    readonly inboundSources: readonly SkillEditorInboundReferenceSource[];
}

const isRecognizableReferenceName = (name: string): boolean =>
    name.length > 0 && name.length <= 255;

export function deriveSkillEditorReferences(
    state: SkillEditorState
): SkillEditorReferences {
    const authoredFiles = state.buffer.files.filter(
        ({ isPlaceholder }) => !isPlaceholder
    );
    const baselineByClientKey = new Map(
        state.baseline.files.map((file) => [file.clientKey, file])
    );
    const referenceNameForFile = (file: (typeof authoredFiles)[number]) => {
        const baseline = baselineByClientKey.get(file.clientKey);
        if (
            baseline &&
            normalizeSkillEditorText(file.name) ===
                normalizeSkillEditorText(baseline.name)
        ) {
            return baseline.name;
        }
        return normalizeSkillEditorText(file.name);
    };
    const vocabulary: string[] = [];
    const seenNames = new Set<string>();
    for (const name of [
        ...authoredFiles.map(referenceNameForFile),
        ...state.baseline.files.map((file) => file.name),
    ]) {
        if (!isRecognizableReferenceName(name) || seenNames.has(name)) {
            continue;
        }
        seenNames.add(name);
        vocabulary.push(name);
    }
    const targets = authoredFiles.flatMap((file) => {
        const name = referenceNameForFile(file);
        return isRecognizableReferenceName(name)
            ? [{ identity: file.clientKey, name }]
            : [];
    });
    const sources = [
        {
            identity: SKILL_INDEX_CLIENT_KEY,
            text: state.buffer.skill.content,
        },
        ...authoredFiles.map((file) => ({
            identity: file.clientKey,
            text: file.content,
        })),
    ];
    const resolved = collectReferenceMentionOccurrences(
        sources,
        vocabulary,
        targets,
        "exact-case"
    );
    const occurrences = resolved.map(
        ({ sourceIdentity, resolvedIdentity, ...mention }) => ({
            ...mention,
            sourceClientKey: sourceIdentity,
            resolvedTargetClientKey: resolvedIdentity,
        })
    );
    const brokenTargets = getBrokenReferenceTargets(resolved).map(
        ({ sourceIdentity, typedTarget }) => ({
            sourceClientKey: sourceIdentity,
            typedTarget,
        })
    );
    const inboundSources: SkillEditorInboundReferenceSource[] = [];

    for (const target of authoredFiles) {
        const baselineName =
            baselineByClientKey.get(target.clientKey)?.name ?? "";
        const countsBySource = new Map<string, number>();
        for (const occurrence of occurrences) {
            const matchesCurrentTarget =
                occurrence.resolvedTargetClientKey === target.clientKey;
            const matchesUnclaimedBaseline =
                occurrence.resolvedTargetClientKey === null &&
                baselineName.length > 0 &&
                occurrence.typedTarget === baselineName;
            if (
                (!matchesCurrentTarget && !matchesUnclaimedBaseline) ||
                occurrence.sourceClientKey === target.clientKey
            ) {
                continue;
            }
            countsBySource.set(
                occurrence.sourceClientKey,
                (countsBySource.get(occurrence.sourceClientKey) ?? 0) + 1
            );
        }
        for (const [sourceClientKey, occurrenceCount] of countsBySource) {
            inboundSources.push({
                targetClientKey: target.clientKey,
                sourceClientKey,
                occurrenceCount,
            });
        }
    }

    return { vocabulary, occurrences, brokenTargets, inboundSources };
}

export function getSkillEditorReferenceCount(
    references: SkillEditorReferences,
    targetClientKey: string
): number {
    return references.inboundSources
        .filter((source) => source.targetClientKey === targetClientKey)
        .reduce((total, source) => total + source.occurrenceCount, 0);
}

export function getSkillEditorRenameReferenceCount(
    references: SkillEditorReferences,
    targetClientKey: string,
    origin: string
): number {
    return references.occurrences.filter(
        ({ resolvedTargetClientKey, typedTarget }) =>
            resolvedTargetClientKey === targetClientKey ||
            (resolvedTargetClientKey === null &&
                origin.length > 0 &&
                typedTarget === origin)
    ).length;
}

export function getSkillEditorInboundSources(
    references: SkillEditorReferences,
    targetClientKey: string
): readonly SkillEditorInboundReferenceSource[] {
    return references.inboundSources.filter(
        (source) => source.targetClientKey === targetClientKey
    );
}

export function getSkillEditorBrokenTargets(
    references: SkillEditorReferences,
    sourceClientKey: string
): readonly SkillEditorBrokenReferenceTarget[] {
    return references.brokenTargets.filter(
        (target) => target.sourceClientKey === sourceClientKey
    );
}
